import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, withModifiers as _withModifiers, withKeys as _withKeys, Transition as _Transition, createVNode as _createVNode, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["aria-labelledby", "aria-describedby"]
const _hoisted_2 = {
  key: 0,
  class: "app-modal__header app-modal__header--custom"
}
const _hoisted_3 = { class: "app-modal__header-content" }
const _hoisted_4 = {
  key: 0,
  class: "app-modal__icon"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "app-modal__text" }
const _hoisted_7 = {
  key: 0,
  id: "modal-title",
  class: "app-modal__title"
}
const _hoisted_8 = {
  key: 1,
  class: "app-modal__subtitle"
}
const _hoisted_9 = ["title"]
const _hoisted_10 = {
  key: 0,
  id: "modal-message",
  class: "app-modal__message"
}
const _hoisted_11 = {
  key: 2,
  class: "app-modal__footer app-modal__footer--custom"
}
const _hoisted_12 = {
  key: 3,
  class: "app-modal__footer"
}
const _hoisted_13 = { class: "app-modal__actions" }

import { ref, nextTick, onUnmounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from './AppButton.vue'

// Define props interface
interface Props {
  title?: string
  subtitle?: string
  message?: string
  icon?: string | object | null
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger' | 'warning' | 'success' | 'outline'
  closeOnBackdropClick?: boolean
  isVisible?: boolean
  show?: boolean
  showClose?: boolean
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  gradientHeader?: boolean
  modern?: boolean
  noPadding?: boolean
  loading?: boolean
  transitionName?: string
  autoFocus?: boolean
}

interface Emits {
  confirm: []
  cancel: []
  close: []
  'update:show': [value: boolean]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
    title: { default: '' },
    subtitle: { default: '' },
    message: { default: '' },
    icon: { default: null },
    confirmText: { default: '' },
    cancelText: { default: '' },
    confirmVariant: { default: 'primary' },
    closeOnBackdropClick: { type: Boolean, default: true },
    isVisible: { type: Boolean, default: false },
    show: { type: Boolean, default: false },
    showClose: { type: Boolean, default: true },
    size: { default: 'medium' },
    gradientHeader: { type: Boolean, default: false },
    modern: { type: Boolean, default: false },
    noPadding: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    transitionName: { default: 'modal' },
    autoFocus: { type: Boolean, default: true }
  },
  emits: ["confirm", "cancel", "close", "update:show"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props

// Define emits interface
const emit = __emit

// i18n
const { t } = useI18n()

// Refs
const modalRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLElement | null>(null)

// Computed visibility - supports both isVisible and show props
const isModalVisible = computed(() => props.isVisible || props.show)

// Methods
const onConfirm = (): void => {
  emit('confirm')
  emit('close')
  emit('update:show', false)
}

const onCancel = (): void => {
  emit('cancel')
  emit('close')
  emit('update:show', false)
}

const handleBackdropClick = (): void => {
  if (props.closeOnBackdropClick && !props.loading) {
    onCancel()
  }
}

const handleEscapeKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape' && !props.loading) {
    onCancel()
  }
}

// Focus trap functionality
const handleTabKey = (event: KeyboardEvent): void => {
  if (!isModalVisible.value || !containerRef.value) return

  const focusableElements = containerRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement?.focus()
    event.preventDefault()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement?.focus()
    event.preventDefault()
  }
}

// Auto-focus management
watch(() => isModalVisible.value, (newVal: boolean) => {
  if (newVal && props.autoFocus) {
    nextTick(() => {
      // Try to focus the close button first, then any focusable element
      if (closeButtonRef.value) {
        (closeButtonRef.value as HTMLElement).focus()
      } else if (containerRef.value) {
        const firstFocusable = containerRef.value.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement
        firstFocusable?.focus()
      }
    })
  }
})

// Prevent body scroll when modal is open
watch(() => isModalVisible.value, (newVal: boolean) => {
  if (newVal) {
    document.body.classList.add('modal-open')
    // Store current scroll position
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
  } else {
    document.body.classList.remove('modal-open')
    // Restore scroll position
    const scrollY = parseInt(document.body.style.top || '0') * -1
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollY)
  }
})

onUnmounted(() => {
  document.body.classList.remove('modal-open')
  // Restore scroll position if component is unmounted while modal is open
  const scrollY = parseInt(document.body.style.top || '0') * -1
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  if (scrollY !== 0) {
    window.scrollTo(0, scrollY)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, {
      name: _ctx.transitionName,
      appear: ""
    }, {
      default: _withCtx(() => [
        (isModalVisible.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["app-modal", [
          `app-modal--${_ctx.size}`,
          { 'app-modal--gradient-header': _ctx.gradientHeader },
          { 'app-modal--modern': _ctx.modern }
        ]]),
              role: "dialog",
              "aria-labelledby": _ctx.title ? 'modal-title' : undefined,
              "aria-describedby": _ctx.message ? 'modal-message' : undefined,
              "aria-modal": "true",
              onClick: handleBackdropClick,
              onKeydown: [
                _withKeys(handleEscapeKey, ["esc"]),
                _withKeys(handleTabKey, ["tab"])
              ],
              ref_key: "modalRef",
              ref: modalRef
            }, [
              _createElementVNode("div", {
                class: "app-modal__container",
                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                ref_key: "containerRef",
                ref: containerRef
              }, [
                (_ctx.$slots.header)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _renderSlot(_ctx.$slots, "header", { close: onCancel })
                    ]))
                  : (_ctx.title || _ctx.subtitle || _ctx.icon || _ctx.showClose)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: _normalizeClass(["app-modal__header", { 'app-modal__header--gradient': _ctx.gradientHeader }])
                      }, [
                        _createElementVNode("div", _hoisted_3, [
                          (_ctx.icon)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                (typeof _ctx.icon === 'string')
                                  ? (_openBlock(), _createElementBlock("svg", {
                                      key: 0,
                                      viewBox: "0 0 24 24",
                                      width: "24",
                                      height: "24",
                                      innerHTML: _ctx.icon
                                    }, null, 8, _hoisted_5))
                                  : (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), { key: 1 }))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_6, [
                            (_ctx.title)
                              ? (_openBlock(), _createElementBlock("h3", _hoisted_7, _toDisplayString(_ctx.title), 1))
                              : _createCommentVNode("", true),
                            (_ctx.subtitle)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_ctx.subtitle), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _cache[1] || (_cache[1] = _createTextVNode()),
                        (_ctx.showClose)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              class: "app-modal__close",
                              onClick: onCancel,
                              title: _ctx.$t('common.close'),
                              ref_key: "closeButtonRef",
                              ref: closeButtonRef
                            }, " ✕ ", 8, _hoisted_9))
                          : _createCommentVNode("", true)
                      ], 2))
                    : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: _normalizeClass(["app-modal__body", { 'app-modal__body--no-padding': _ctx.noPadding }])
                }, [
                  _renderSlot(_ctx.$slots, "default", {}, () => [
                    (_ctx.message)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_ctx.message), 1))
                      : _createCommentVNode("", true)
                  ])
                ], 2),
                (_ctx.$slots.footer)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _renderSlot(_ctx.$slots, "footer", {
                        close: onCancel,
                        confirm: onConfirm
                      })
                    ]))
                  : (!_ctx.$slots.default && (_ctx.confirmText || _ctx.cancelText))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          (_ctx.cancelText)
                            ? (_openBlock(), _createBlock(AppButton, {
                                key: 0,
                                type: "button",
                                variant: "outline",
                                onClick: onCancel,
                                class: "app-modal__btn app-modal__btn--cancel",
                                disabled: _ctx.loading
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.cancelText), 1)
                                ]),
                                _: 1
                              }, 8, ["disabled"]))
                            : _createCommentVNode("", true),
                          (_ctx.confirmText)
                            ? (_openBlock(), _createBlock(AppButton, {
                                key: 1,
                                type: "button",
                                variant: _ctx.confirmVariant,
                                onClick: onConfirm,
                                class: "app-modal__btn app-modal__btn--confirm",
                                disabled: _ctx.loading,
                                loading: _ctx.loading
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.confirmText), 1)
                                ]),
                                _: 1
                              }, 8, ["variant", "disabled", "loading"]))
                            : _createCommentVNode("", true)
                        ])
                      ]))
                    : _createCommentVNode("", true)
              ], 512)
            ], 42, _hoisted_1))
          : _createCommentVNode("", true)
      ]),
      _: 3
    }, 8, ["name"])
  ]))
}
}

})